import { Search, X } from 'lucide-react';
import type { MemoryCategory, MemorySource } from '@/modules/shared/types/yc-demo';

export type CategoryFilter = MemoryCategory | 'all';
export type SourceFilter = MemorySource | 'all';

const CATEGORY_OPTIONS: { value: CategoryFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'scoring', label: 'Scoring' },
  { value: 'sourcing', label: 'Sourcing' },
  { value: 'pipeline', label: 'Pipeline' },
  { value: 'culture', label: 'Culture' },
  { value: 'compensation', label: 'Compensation' },
];

const SOURCE_OPTIONS: { value: SourceFilter; label: string }[] = [
  { value: 'all', label: 'All sources' },
  { value: 'score_edit', label: 'Score edits' },
  { value: 'pipeline_outcome', label: 'Outcomes' },
  { value: 'observed', label: 'Observed' },
  { value: 'manual', label: 'Manual' },
];

export function MemoryFilterBar({
  category,
  onCategoryChange,
  source,
  onSourceChange,
  query,
  onQueryChange,
  categoryCounts,
  sourceCounts,
}: {
  category: CategoryFilter;
  onCategoryChange: (next: CategoryFilter) => void;
  source: SourceFilter;
  onSourceChange: (next: SourceFilter) => void;
  query: string;
  onQueryChange: (next: string) => void;
  categoryCounts: Record<CategoryFilter, number>;
  sourceCounts: Record<SourceFilter, number>;
}) {
  return (
    <div className="memory-filter-bar">
      <div className="memory-filter-row">
        {CATEGORY_OPTIONS.map((opt) => {
          const count = categoryCounts[opt.value] ?? 0;
          const active = category === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              className={active ? 'memory-chip memory-chip-active' : 'memory-chip'}
              onClick={() => onCategoryChange(opt.value)}
              aria-pressed={active}
            >
              <span>{opt.label}</span>
              <span className="memory-chip-count">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="memory-filter-row">
        {SOURCE_OPTIONS.map((opt) => {
          const count = sourceCounts[opt.value] ?? 0;
          const active = source === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              className={active ? 'memory-chip memory-chip-soft memory-chip-active' : 'memory-chip memory-chip-soft'}
              onClick={() => onSourceChange(opt.value)}
              aria-pressed={active}
            >
              <span>{opt.label}</span>
              <span className="memory-chip-count">{count}</span>
            </button>
          );
        })}
      </div>

      <label className="memory-search">
        <Search size={14} strokeWidth={1.8} />
        <input
          type="search"
          placeholder="Search insights, triggers, or attribution"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
        />
        {query && (
          <button
            type="button"
            className="memory-search-clear"
            onClick={() => onQueryChange('')}
            aria-label="Clear search"
          >
            <X size={12} strokeWidth={2} />
          </button>
        )}
      </label>
    </div>
  );
}
